import { createRootRoute, Outlet } from '@tanstack/react-router';
import { AuthKitProvider } from '@workos-inc/authkit-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PostHogProvider } from 'posthog-js/react';
import { Toaster, ToastBar, useToaster } from '@/lib/toast';
import { useMediaQuery } from '@/hooks/use-media-query';
import { ThemeProvider } from '@/components/theme-provider';
import { ErrorBoundary } from '@/components/error-boundary';
import { RouteError } from '@/components/route-error';
import { ApiAuthProvider } from '@/components/api-auth-provider';
import { AttributionTracker } from '@/components/attribution-tracker';
import { OrgEnrollmentTracker } from '@/components/org-enrollment-tracker';
import { MarketingAnalytics } from '@/components/analytics/marketing-analytics';
import { PerformanceTracker } from '@/components/performance-tracker';
import { getRenderMode } from '@/lib/analytics';
import { AuthError } from '@/lib/errors';
import { useSessionExpiry } from '@/hooks/use-session-expiry';

function SessionExpiryWatcher() {
  useSessionExpiry();
  return null;
}

const toastStyle: import('react').CSSProperties = {
  background: 'var(--card)',
  color: 'var(--card-foreground)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  overflow: 'hidden',
  position: 'relative',
};

function ResponsiveToaster() {
  const isMobile = useMediaQuery('(max-width: 639px)');
  return (
    <Toaster
      position={isMobile ? 'bottom-center' : 'top-right'}
      containerStyle={isMobile ? { bottom: 88 } : undefined}
      toastOptions={{ style: toastStyle }}
    >
      {(t) => (
        <ToastBar toast={t} style={toastStyle}>
          {({ icon, message }) => (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {icon}
                {message}
              </div>
              {t.visible && (
                <span
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: t.type === 'error' ? 'var(--destructive)' : 'oklch(0.6 0.18 145)',
                    transformOrigin: 'left',
                    animation: `toast-progress ${t.duration ?? 4000}ms linear forwards`,
                  }}
                />
              )}
              <style>{`@keyframes toast-progress { from { transform: scaleX(1); } to { transform: scaleX(0); } }`}</style>
            </>
          )}
        </ToastBar>
      )}
    </Toaster>
  );
}

function MobileToastBackdrop() {
  const { toasts } = useToaster();
  const hasVisible = toasts.some((t) => t.visible);
  if (!hasVisible) return null;
  return <div className="fixed inset-0 bg-black/30 sm:hidden" style={{ zIndex: 9998 }} />;
}

/**
 * Configure TanStack Query with optimized defaults
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: 5 minutes for most data
      // Data is considered fresh for 5 minutes before refetching
      staleTime: 1000 * 60 * 5,

      // Garbage collection time: 10 minutes
      // Unused data is kept in cache for 10 minutes
      gcTime: 1000 * 60 * 10,

      // Retry failed requests 3 times with exponential backoff
      retry: (failureCount, error) => {
        // Don't retry on auth errors (401/403 thrown as AuthError by api-client)
        if (error instanceof AuthError) return false;
        // Don't retry on 4xx errors (client errors)
        if (error instanceof Error && 'statusCode' in error) {
          const statusCode = error.statusCode;
          if (typeof statusCode === 'number' && statusCode >= 400 && statusCode < 500) {
            return false;
          }
        }
        return failureCount < 3;
      },

      // Retry delay with exponential backoff: 1s, 2s, 4s
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Refetch on window focus for real-time updates (important for tasks)
      refetchOnWindowFocus: true,

      // Don't refetch on mount if data is still fresh
      refetchOnMount: false,

      // Refetch on reconnect to get latest data
      refetchOnReconnect: true,

      // Network mode: online/offline handling
      networkMode: 'online',
    },
    mutations: {
      // Retry mutations only once (POST/PATCH aren't always idempotent)
      retry: 1,

      // Network mode for mutations
      networkMode: 'online',
    },
  },
});

function RootComponent() {
  return (
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN!}
      options={{
        api_host: 'https://f.boundr.in',
        ui_host: 'https://us.posthog.com',
        defaults: '2026-05-30',
        capture_exceptions: true,
        // Core Web Vitals (LCP/FCP/CLS/INP). The web-vitals extension is
        // self-hosted (see vite.config copyPosthogAssets + index.html) so it
        // works alongside disable_external_dependency_loading.
        capture_performance: { web_vitals: true },
        disable_external_dependency_loading: true,
        // Tag every event with how the current document was rendered (ssg/csr)
        // so performance, engagement, and pageviews can be segmented by it.
        before_send: (event) => {
          if (event) event.properties = { ...event.properties, render_mode: getRenderMode() };
          return event;
        },
        debug: import.meta.env.DEV,
        // Disabled in local dev and CI; enabled only in beta/prod (baked by vite.config.ts).
        opt_out_capturing_by_default: !import.meta.env.VITE_POSTHOG_ENABLED,
      }}
    >
      <ErrorBoundary>
        <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
          <AuthKitProvider
            clientId={import.meta.env.VITE_WORKOS_CLIENT_ID}
            onRedirectCallback={({ state }) => {
              // WorkOS SDK calls window.history.replaceState('/') immediately after
              // this callback returns to strip the ?code= param. Because TanStack Router
              // patches history.replaceState, that triggers a re-route to '/' which
              // overrides any navigate() call made here. Instead, persist the destination
              // so the Index component can redirect after the SDK cleanup completes.
              const parseState = (s: unknown): string | null => {
                if (typeof s !== 'string') return null;
                // WorkOS SDK JSON.parses state before calling us, but guard either way.
                try {
                  const parsed = JSON.parse(s);
                  return typeof parsed === 'string' ? parsed : s;
                } catch {
                  return s;
                }
              };
              const isValidRedirect = (p: unknown): p is string =>
                typeof p === 'string' && p.startsWith('/') && !p.startsWith('/login');

              const parsedState = parseState(state);
              const storedPath = sessionStorage.getItem('redirectAfterLogin');
              sessionStorage.removeItem('redirectAfterLogin');

              const redirectTo = isValidRedirect(parsedState)
                ? parsedState
                : isValidRedirect(storedPath)
                  ? storedPath
                  : '/dashboard';

              sessionStorage.setItem('postAuthRedirect', redirectTo);
            }}
          >
            <ApiAuthProvider>
              <QueryClientProvider client={queryClient}>
                <SessionExpiryWatcher />
                <AttributionTracker />
                <OrgEnrollmentTracker />
                <MarketingAnalytics />
                <PerformanceTracker />
                <Outlet />
                <MobileToastBackdrop />
                <ResponsiveToaster />
                <ReactQueryDevtools initialIsOpen={false} />
              </QueryClientProvider>
            </ApiAuthProvider>
          </AuthKitProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </PostHogProvider>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  errorComponent: RouteError,
});
